/**
 * OKS-21 — Inertia prop shapes for invite link generation + tracking.
 *
 * Locked at TRD/Spec publish; reconciled with Backend TRD §API Surface.
 * Snake_case throughout — mirrors the Rails Inertia serialiser 1:1.
 *
 * Surfaces:
 *   - `pages/Brand/Candidates/Show.tsx` (extends OKS-18 props with
 *     `invite` + `invite_actions`)
 *   - `pages/Brand/Candidates/Index.tsx` (Inertia <Deferred> on
 *     `funnel`)
 *   - `pages/Public/InviteLanding.tsx` (3-state collapsed prop —
 *     server merges expired + revoked + completed → "invalid")
 *   - `pages/Brand/Settings/Invites.tsx` (single numeric form field)
 *
 * **P0 brand-isolation contract:** `InviteLandingProps.brand` exposes
 * ONLY `name` / `logo_url` / `locale`. No `brand_id`, no `slug`, no
 * candidate fields. The 3-state enum collapse happens server-side in
 * `Public::InviteLandingsController#show` — wire-visible state would
 * be a disclosure side channel, regardless of FE rendering.
 */

// ─── Admin-side full lifecycle enum (6 values) ─────────────────────

/**
 * Server-derived from nullable timestamps + `expires_at`. The brand
 * admin sees the full enum on the Invites panel; the public landing
 * collapses to 3 values.
 *
 * Order is canonical pipeline order — used by `<InviteFunnelWidget>`
 * for stage display.
 */
export type InviteState =
  | "generated"
  | "clicked"
  | "signup_started"
  | "completed"
  | "expired"
  | "revoked"

/**
 * Funnel stages = first 4 lifecycle states (the terminal happy path).
 * `expired` and `revoked` are admin-side states that don't appear in
 * the funnel viz.
 */
export type FunnelStage =
  | "generated"
  | "clicked"
  | "signup_started"
  | "completed"

export const FUNNEL_STAGES: readonly FunnelStage[] = [
  "generated",
  "clicked",
  "signup_started",
  "completed",
] as const

// ─── Brand-side: Invites panel (Candidate Detail) ──────────────────

export interface InviteSummary {
  id: number
  state: InviteState
  /**
   * Public invite URL. OKS-655 — this is now the SHORT front-door link
   * (`https://<APP_HOST>/invite/<short_code>`) which 302-redirects to the
   * tokenized landing; the long opaque-token URL is no longer surfaced.
   * Copy/Share bind to this value verbatim.
   */
  url: string
  /** ISO8601, brand TZ. */
  generated_at: string
  expires_at: string
  first_clicked_at: string | null
  last_clicked_at: string | null
  click_count: number
  signup_started_at: string | null
  completed_at: string | null
  revoked_at: string | null
}

/**
 * Action affordances on the Invites panel. Each URL is `string | null`;
 * `null` is the **single visibility discriminator** — the affordance is
 * either state-illegal (e.g. `revoke_url` when no active invite) or
 * role-locked. FE renders DOM-absent in both cases (no
 * disabled-with-tooltip path).
 *
 * Mirrors the OKS-19 `CandidateActions` pattern — single source of
 * truth on visibility decisions lives server-side.
 */
export interface InviteActions {
  /** Non-null when candidate is `approved` (PRD §3 — first generate
   *  flips status to `invited`). Idempotent regenerate uses same URL. */
  generate_url: string | null
  /** Non-null when there's an active (non-terminal) invite. */
  revoke_url: string | null
}

// ─── Funnel widget + day table (Candidates Index) ──────────────────

export interface FunnelDayBucket {
  /** "YYYY-MM-DD" — brand-TZ calendar day. */
  date: string
  generated: number
  clicked: number
  signup_started: number
  completed: number
}

export interface FunnelTotals {
  generated: number
  clicked: number
  signup_started: number
  completed: number
}

/**
 * Step-to-step conversion ratios as floats in [0, 1].
 * `0` (not NaN) when the prior stage is empty — backend guarantees this.
 */
export interface FunnelConversion {
  gen_to_click: number
  click_to_signup: number
  signup_to_complete: number
}

export interface FunnelData {
  /** IANA TZ identifier, e.g. "Europe/Paris". */
  brand_timezone: string
  /** ISO date inclusive (YYYY-MM-DD). */
  range_start: string
  /** ISO date inclusive (YYYY-MM-DD). */
  range_end: string
  totals: FunnelTotals
  conversion: FunnelConversion
  /** One bucket per brand-TZ calendar day in the range. */
  daily: FunnelDayBucket[]
}

/**
 * URL-driven range filter state echoed back by the controller. Mirrors
 * the OKS-17 `?param` pattern. Default range = last 30 days in brand TZ.
 */
export type FunnelRangePreset = "7d" | "30d" | "custom"

// ─── Public landing (`/i/:token`) ──────────────────────────────────

/**
 * **3-state collapsed enum** (server-side merge in
 * `Public::InviteLandingsController#show`). PRD §6 + side-channel
 * mitigation:
 *
 *   - `valid` — link is live; `cta_url`/`inviter_first_name`/`expires_at` non-null
 *   - `invalid` — collapsed terminal (`expired`+`revoked`+`completed`)
 *     OR token signature/lookup miss. **Indistinguishable on the wire.**
 *   - `identity_mismatch` — set when OKS-22/23 redirects back here on
 *     OAuth handle mismatch; PRD §3 explicitly discloses this state.
 *
 * QA's negative-assertion (E10) verifies no `error-expired/-revoked
 * /-completed` testids ever render — guarded by this 3-state contract.
 */
export type InviteLandingState =
  | "valid"
  | "invalid"
  | "identity_mismatch"
  | "recognised_email"
  | "role_mismatch"

/**
 * Brand identity surfaced on the public landing. **Locked minimal
 * shape** — only display fields, no IDs/slugs. Same
 * `cross_brand_leak_matcher` (OKS-18) negative assertion applies.
 */
export interface InviteLandingBrand {
  name: string
  logo_url: string | null
  /** Brand's default locale; consumed by `lib/i18n-public.ts` as
   *  fallback when `navigator.language` doesn't resolve to a Crew
   *  locale and `?lang=` is absent. */
  locale: "en" | "fr"
}

/**
 * OKS-383 — One brand value card on the invite landing. Resolved-with-
 * fallback server-side (authored strings, or the platform default
 * localized in the brand's locale), so `title`/`body` are always
 * ready-to-render. `icon_key` is a catalog key the FE maps to a Lucide
 * glyph (`lib/charter-value-icons.ts`); `null` renders the ordinal
 * "01/02/03" badge. Disclosure-safe — pure brand-authored text.
 */
export interface InviteValueCard {
  title: string
  body: string
  icon_key: string | null
}

/**
 * OKS-383 — Inviter identity for the `valid` landing (PRD AC1).
 * Initials-only — Crew admins have no profile-photo column yet (PRD
 * Q1), so the FE renders an initials monogram. Disclosure-safe: name +
 * initials only, no user id / email.
 */
export interface InviteInviter {
  name: string
  initials: string
}

export interface InviteLandingProps {
  brand: InviteLandingBrand
  state: InviteLandingState
  /**
   * OKS-457 — first-view-only §00 brand splash flag. `true` ONLY on the
   * first `valid` view for this device (server reads-then-marks a
   * per-invite cookie `oks_splash_seen_<invite_id>`); a reload/return
   * ships `false` so the splash never replays. Always `false` on
   * dead-end states. The FE mounts `<BrandSplash>` only when
   * `show_splash && state === "valid"`. No localStorage (Crew rule) —
   * this prop is the single source of truth.
   */
  show_splash: boolean
  /**
   * OKS-457 — living-crew social proof (§01 teaser). Brand-scoped
   * aggregate COUNT of creators currently on the brand's crew
   * (`active` + `pending_charter`). FLAT sibling scalar (mirrors
   * `inviter`/`value_cards`). `null` on every non-`valid` state.
   * Aggregate-only — carries zero member identity (brand isolation P0).
   */
  crew_count?: number | null
  /**
   * OKS-457 — living-crew social proof (§01 teaser). Brand-scoped
   * aggregate COUNT of invite-link views in the current calendar month.
   * `null` on every non-`valid` state. Aggregate-only.
   */
  views_this_month?: number | null
  /**
   * OKS-347 — Brand cover image URL for the `<BrandCoverBand>` header.
   * **TOP-LEVEL sibling of `brand`** — deliberately NOT nested inside
   * `InviteLandingBrand`, whose `{name, logo_url, locale}` shape is
   * disclosure-locked by the OKS-303 request spec. Display-only storage
   * proxy URL (`mode: :proxy` server-side, for unauthenticated
   * open-link-later durability); carries no `brand_id`/`slug`. `null`
   * when the brand has no cover OR the token resolves no brand
   * (forged/garbage) → the band renders its dark monogram fallback.
   * Present even on terminal states (expired/revoked) while a brand is
   * still bound.
   */
  cover_image_url: string | null
  /**
   * OKS-857 — top-level sibling: brand portrait (9:16) splash background
   * URL. Same disclosure-safe treatment as `cover_image_url` (`mode: :proxy`,
   * signed storage URL, no `brand_id`/`slug`). Takes precedence over
   * `cover_image_url` on the splash (`splashCoverUrl ?? coverUrl`); `null`
   * when the brand has no portrait image or the token resolves no brand →
   * the splash falls back to the cover, then the dark backdrop.
   */
  splash_cover_image_url: string | null
  /** OKS-466 — POST action for the "Continue with TikTok" signup start
   *  (`/signup/tiktok/start`); null when `state !== "valid"`. The CTA is
   *  a NATIVE form POST (cross-origin OAuth nav), not an Inertia visit. */
  cta_url: string | null
  /** OKS-466 — signed invite token, sent as the hidden `token` field of
   *  the "Continue with TikTok" form. Null on non-`valid` states. */
  cta_token: string | null
  /** Personalized intro (`{inviter} from {brand_name} has invited you…`).
   *  Falls back to brand name client-side when null. Null on
   *  non-`valid` states (avoids leaking inviter timing). */
  inviter_first_name: string | null
  /** OKS-383 — inviter identity on the `valid` landing (PRD AC1).
   *  Null when there's no resolvable inviter (drop the "From …" line)
   *  or on non-`valid` states. */
  inviter?: InviteInviter | null
  /** OKS-383 — exactly three brand value cards, resolved-with-fallback
   *  server-side. Null on non-`valid` states. */
  value_cards?: InviteValueCard[] | null
  /** OKS-383 — invitee display name for the eyebrow. Null on
   *  non-`valid` states or when the candidate has no name (eyebrow
   *  falls back to "You're invited"). */
  candidate_name?: string | null
  /** ISO8601; null when `state !== "valid"` (avoids leaking expiry
   *  timing as a side channel for the `invalid` collapse). */
  expires_at: string | null
  /**
   * OKS-24 → OKS-675 — `recognised_email` state props. The invitee email
   * is NEVER emitted (privacy P0 — `recognised_user` + `sign_in_url` were
   * removed). Recognised members now render the full brand welcome
   * (personalised via `candidate_name`) and join in place. Server fills
   * exactly one CTA per session sub-variant:
   *
   *  - logged-IN match: `accept_url` (one-tap "Join {brand}'s crew",
   *      POST /i/:token/accept) + `sign_out_url` (escape hatch). Full
   *      welcome props (`inviter`/`value_cards`/`cover`/`crew_count`…).
   *  - logged-OUT: `cta_url` + `cta_token` (native "Continue with TikTok"
   *      start form; the OAuth callback completes the join). Full welcome
   *      props.
   *  - wrong-account (logged-IN as a different user): `sign_out_url` only,
   *      no welcome (dead-end logout card).
   *
   * All null when `state !== "recognised_email"`.
   */
  accept_url?: string | null
  sign_out_url?: string | null
  /**
   * OKS-24 — `role_mismatch` state. Server-side role check on matched user
   * BEFORE the recognised-email branch — non-creator authenticated user
   * (brand/agency/admin) gets this state. `current_role` is the role of
   * the signed-in account so the body copy can spell it out.
   *
   * Null when `state !== "role_mismatch"`.
   */
  current_role?: string | null
  /**
   * OKS-24 — `identity_mismatch` state context discriminator. UX Spec
   * §12.1 fork on the shared `invite-landing-error-mismatch` testid via
   * `data-mismatch-context`. Values: `"anon"` (OKS-21 original) |
   * `"recognised"` (Flow E — second-brand identity check fails BEFORE
   * mutation). Server emits this on the public landing path; the
   * dedicated dead-end page (`/onboarding/identity_mismatch`) emits its
   * own `"post_oauth"` value.
   *
   * Null/absent for non-mismatch states.
   */
  mismatch_context?: "anon" | "recognised" | null
  /**
   * OKS-24 — TikTok handle to surface in identity-mismatch copy. For
   * `mismatch_context="anon"` this is the TikTok-session handle; for
   * `mismatch_context="recognised"` it's the matched user's verified
   * OKS-side handle (so the user understands the brand-side handle they
   * registered with doesn't match the candidate the brand invited).
   *
   * Null/absent when `state !== "identity_mismatch"`.
   */
  mismatch_handle?: string | null
  /**
   * OKS-24 — server-resolved support `mailto:` URL surfaced on the
   * `recognised` mismatch CTA. Falls back to `null` on `anon` (OKS-21
   * retry-CTA shape doesn't use mailto).
   */
  mismatch_support_url?: string | null
}

// ─── Brand Settings → Invites ──────────────────────────────────────

/**
 * Single-field settings page. Range 1..90 enforced both server-side
 * (Brand model validator) and client-side (`<input min="1" max="90">`).
 *
 * Form binding: `name="brand[invite_link_expiration_days]"` — frozen
 * by Vitest wire-format canary per project memory
 * `vitest_wire_format_canary`.
 */
export interface BrandSettingsInvitesProps {
  brand: {
    invite_link_expiration_days: number
  }
  /**
   * BugFix D18 (CTPO QA): server-emitted policy gate. `false` for
   * brand-tier `member` users (read-only access — policy permits
   * `show?` but not `update?`). FE hides Save + sets the input
   * `readonly` when this is `false`. Mirrors OKS-19's
   * `actions.{verb}_url: string|null` pattern — server gates the
   * affordance, FE renders. Computed via
   * `BrandSettingsInvitesPolicy#update?` so PATCH and the prop can
   * never drift.
   */
  can_edit: boolean
}

/** Range cap — mirrored in FE `<input min max>` + Vitest spec. */
export const INVITE_EXPIRATION_MIN_DAYS = 1
export const INVITE_EXPIRATION_MAX_DAYS = 90

/**
 * OKS-956 — one row of the time-in-stage table.
 *
 * `stage` is a `Candidate::STATUSES` value, labelled on the FE via the shipped
 * `candidates.status.*` keys. Percentiles are days, already rounded to one
 * decimal server-side; the FE formats them for locale but never recomputes.
 */
export interface StageTimeRow {
  stage: string
  p50_days: number | null
  p90_days: number | null
}

/**
 * OKS-956 — the `stage_analytics` deferred prop.
 *
 * ⚠️ `loading` is deliberately absent from `state`: it is not a server outcome
 * but the deferred-pending window, owned by `<Deferred fallback>`.
 *
 * ⚠️ The prop itself is `null` when the funnel gate refuses the viewer, which
 * is NOT `state: "error"` — a refused viewer renders nothing at all. The two
 * are distinct on the wire; see `CandidatesController#stage_analytics_props`.
 *
 * This interface encodes only the FE's side of that contract. What proves the
 * server actually sends this shape is the request spec, not `tsc`.
 */
export interface StageAnalyticsSection {
  state: "populated" | "empty" | "error"
  rows: StageTimeRow[]
}
